import { z } from 'zod';
import {
  MAX_FILE_SIZE,
  MAX_FILES,
  MAX_TOTAL_SIZE,
  ACCEPTED_FILE_TYPES,
  getProviderAssignmentError,
  getAnesthesiaWindowIssues,
  getProviderMode,
  parseLocalDate,
  today,
  isValidDate,
  DEFAULT_ENCOUNTER_FIELDS_CONFIG,
} from '@claim-os/shared';
import type { FormFieldSetting } from '@claim-os/shared';

// ---------------------------------------------------------------------------
// Static building blocks (config-independent) — feature 043 extracted these
// out of schema.ts so both the default (static) sub-schemas and this
// config-driven factory share one definition.
// ---------------------------------------------------------------------------

const providerRoleValues = [
  'PRIMARY_SURGEON', 'ASSISTANT_SURGEON', 'PHYSICIANS_ASSISTANT',
  'ONE_CO_SURGEON', 'BOTH_CO_SURGEONS', 'CO_SURGEON_1', 'CO_SURGEON_2',
  // Feature 044 — replaced the anesthesia PROVIDER/SUPERVISOR pair. The old codes are
  // deliberately absent: there are no production anesthesia claims to accept them from.
  'ANESTHESIOLOGIST', 'CRNA', 'DEFAULT',
] as const;

export const providerAssignmentSchema = z.object({
  providerId: z.string().min(1, 'Provider is required'),
  providerName: z.string(),
  role: z.enum(providerRoleValues),
  credential: z.string().optional(),
  // Carried through validation so the save payload keeps it — a zod object strips
  // unknown keys, so leaving npi out here silently dropped it from every save.
  npi: z.string().optional(),
});

// Format rules skip validation on an empty string — codes are optional per-row, so a
// not-yet-typed row shouldn't error (mirrors the notInFuture empty-skip pattern below).
const cptCodePattern = /^.{5}$/;
const asaCodePattern = /^\d{5}$/;

export const cptCodeLineSchema = z.object({
  code: z.string().refine((v) => !v || cptCodePattern.test(v), 'CPT code must be exactly 5 characters'),
  modifiers: z.array(z.string()),
});

export const asaCodeLineSchema = z.object({
  code: z.string().refine((v) => !v || asaCodePattern.test(v), 'ASA code must be exactly 5 digits'),
  modifiers: z.array(z.string()),
});

// The anesthesia window is four form fields (feature 044); they are composed into the two
// persisted instants (`anesthesiaStart`/`anesthesiaEnd`) by the submit service.
export const codingSchema = z.object({
  icd10: z.array(z.string()),
  cpt: z.array(cptCodeLineSchema),
  asaClassification: z.string(),
  anesthesiaStartDate: z.string(),
  anesthesiaStartTime: z.string(),
  anesthesiaEndDate: z.string(),
  anesthesiaEndTime: z.string(),
  asaCodes: z.array(asaCodeLineSchema),
});

export const defaultCoding = {
  icd10: [] as string[],
  cpt: [] as { code: string; modifiers: string[] }[],
  asaClassification: '',
  anesthesiaStartDate: '',
  anesthesiaStartTime: '',
  anesthesiaEndDate: '',
  anesthesiaEndTime: '',
  asaCodes: [] as { code: string; modifiers: string[] }[],
};

// Coding is per-claim: each selected claim type carries its own coding object
// (ICD-10 + CPT, plus ASA for Anesthesia) rather than one shared off the encounter root.
export const claimTypeAssignmentSchema = z.object({
  type: z.string().min(1),
  providers: z.array(providerAssignmentSchema),
  coding: codingSchema.default(defaultCoding),
});

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(0))} ${sizes[i]}`;
}

// Size / total-size / file-type refinements shared by the required and optional
// documents variants — only the min(1) count differs between them.
const withDocumentRefinements = (schema: z.ZodType<File[]>) =>
  schema
    .refine(
      (files) => files.every((file) => file.size <= MAX_FILE_SIZE),
      (files) => {
        const oversized = files.find((f) => f.size > MAX_FILE_SIZE);
        return { message: `${oversized?.name ?? 'File'} exceeds the ${formatFileSize(MAX_FILE_SIZE)} limit` };
      }
    )
    .refine(
      (files) => files.reduce((sum, f) => sum + f.size, 0) <= MAX_TOTAL_SIZE,
      `Total file size exceeds ${formatFileSize(MAX_TOTAL_SIZE)} limit`
    )
    .refine(
      (files) => files.every((file) => (ACCEPTED_FILE_TYPES as readonly string[]).includes(file.type)),
      (files) => {
        const invalid = files.find((f) => !(ACCEPTED_FILE_TYPES as readonly string[]).includes(f.type));
        return { message: `${invalid?.name ?? 'File'} is not a supported file type. Accepted: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX` };
      }
    );

const documentsFileArray = z.array(z.instanceof(File)).max(MAX_FILES, `Maximum ${MAX_FILES} files allowed`);

/** Documents when required — at least one file (today's default). */
export const documentsSchema = withDocumentRefinements(
  documentsFileArray.min(1, 'At least one document is required'),
);

/** Documents when the practice makes them not-required — zero files allowed (identity anchored on patient name). */
const optionalDocumentsSchema = withDocumentRefinements(documentsFileArray);

// ---------------------------------------------------------------------------
// Config-driven factory (feature 043 — practice settings)
// ---------------------------------------------------------------------------

/** Not-in-the-future check that skips validation on an empty (optional, unfilled) value. */
function notInFuture(val: string): boolean {
  if (!val) return true;
  const d = parseLocalDate(val);
  return isValidDate(d) && d <= today();
}

/**
 * Builds the encounter-level submit-claim schema from a practice's
 * `encounterFields` config. Every configurable field drops its `min(1)`/required
 * constraint when configured not-required — including firstName/lastName/dateOfBirth/
 * dateOfService and, per the 2026-07-16 amendment, the three structural anchors:
 * `documents` (min-1-file), `billType` (≥1 claim type), and `provider` (≥1 provider
 * per case). `isGapCase` becomes optional. The settings save guarantees at least one
 * identity anchor stays required, so a resolved config can never require nothing.
 * All existing cross-field refinements (DOS≥DOB, surgeon-conflict, ASA-required-
 * when-Anesthesia) are preserved verbatim. The anesthesia window rules changed in
 * feature 044 — complete-or-absent and end-strictly-after-start over four fields,
 * replacing the end-time-vs-start-time cross-day comparison. The ASA/anesthesia checks
 * apply per-claim-type since coding moved to the claim level, gated by `claimTypeFields`
 * (feature 043 US3) — only ICD-10 and ASA classification can be required per the catalog;
 * everything else (CPT, the anesthesia window, ASA codes) is visible-only.
 */
export function buildSubmitClaimSchema(
  encounterFields: Record<string, FormFieldSetting>,
  claimTypeFields: Record<string, Record<string, FormFieldSetting>> = DEFAULT_ENCOUNTER_FIELDS_CONFIG.claimTypeFields,
) {
  const isRequired = (key: string) => encounterFields[key]?.required ?? true;

  const baseSchema = z.object({
    // Patient Details
    firstName: isRequired('firstName') ? z.string().min(1, 'First name is required') : z.string(),
    lastName: isRequired('lastName') ? z.string().min(1, 'Last name is required') : z.string(),
    dateOfBirth: isRequired('dateOfBirth')
      ? z.string().min(1, 'Date of birth is required').refine(notInFuture, 'Date of birth cannot be in the future')
      : z.string().refine(notInFuture, 'Date of birth cannot be in the future'),
    medicalRecordNumber: isRequired('medicalRecordNumber')
      ? z.string().min(1, 'Medical record number is required')
      : z.string(),
    insurer: isRequired('insurer') ? z.string().min(1, 'Insurer is required') : z.string(),
    memberId: isRequired('memberId') ? z.string().min(1, 'Member ID is required') : z.string(),
    // Claim types (bill type) — requiredness configurable per practice.
    claimTypes: isRequired('billType')
      ? z.array(claimTypeAssignmentSchema).min(1, 'Please select at least one claim type')
      : z.array(claimTypeAssignmentSchema),
    // Encounter Information
    dateOfService: isRequired('dateOfService')
      ? z.string().min(1, 'Date of service is required').refine(notInFuture, 'Date of service cannot be in the future')
      : z.string().refine(notInFuture, 'Date of service cannot be in the future'),
    location: isRequired('location') ? z.string().min(1, 'Location is required') : z.string(),
    isGapCase: isRequired('isGapCase')
      ? z.boolean({
          required_error: 'Please indicate whether this is a gap case',
          invalid_type_error: 'Please indicate whether this is a gap case',
        })
      : z.boolean().optional(),
    // Documents — requiredness configurable per practice.
    documents: isRequired('documents') ? documentsSchema : optionalDocumentsSchema,
  });

  const submitClaimSchema = baseSchema
    .superRefine((data, ctx) => {
      // Role conflicts + one-role-per-provider, both per claim type.
      data.claimTypes.forEach((entry, i) => {
        const providerError = getProviderAssignmentError(entry.providers);
        if (providerError) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['claimTypes', i, 'providers'], message: providerError });
        }
      });

      // Provider requiredness is configurable per practice (feature 043 amendment).
      if (isRequired('provider')) {
        const hasAnyProvider = data.claimTypes.some((ct) => ct.providers.length > 0);
        const hasNoneModeCase = data.claimTypes.some((ct) => getProviderMode(ct.type) === 'none');
        if (!hasAnyProvider && !hasNoneModeCase) {
          const firstRolesIdx = data.claimTypes.findIndex((ct) => getProviderMode(ct.type) !== 'none');
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['claimTypes', firstRolesIdx >= 0 ? firstRolesIdx : 0, 'providers'],
            message: 'At least one provider is required for this claim type.',
          });
        }
      }

      // Coding is per-claim, gated by claimTypeFields[type] (feature 043 US3).
      data.claimTypes.forEach((entry, i) => {
        const c = entry.coding;
        if (!c) return;
        const fields = claimTypeFields[entry.type];

        if (fields?.icd10?.required && c.icd10.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['claimTypes', i, 'coding', 'icd10'],
            message: 'ICD-10 / Diagnosis is required for this claim type.',
          });
        }

        if (entry.type !== 'ANESTHESIA') return;

        if ((fields?.asaClassification?.required ?? true) && !c.asaClassification) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['claimTypes', i, 'coding', 'asaClassification'],
            message: 'ASA classification is required for anesthesia claims',
          });
        }

        // The anesthesia window (feature 044): complete-or-absent, end strictly after
        // start, with the same-date typo called out on the end time. The rule set is
        // shared with edit-claim and the back office.
        getAnesthesiaWindowIssues(c).forEach(({ field, message }) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['claimTypes', i, 'coding', field],
            message,
          });
        });
      });
    })
    // Cross-field: DOS must not be before DOB. Using .refine() on the flat
    // ZodObject ensures zodResolver propagates the error on every onChange.
    .refine(
      (data) => {
        if (!data.dateOfBirth || !data.dateOfService) return true;
        const dob = parseLocalDate(data.dateOfBirth);
        const dos = parseLocalDate(data.dateOfService);
        if (!isValidDate(dob) || !isValidDate(dos)) return true;
        return dos >= dob;
      },
      { message: 'Date of service cannot be before patient date of birth', path: ['dateOfService'] }
    );

  return { baseSchema, submitClaimSchema };
}
